import { call, put, race, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { monolithApiUrl } from '../../config/app';
import { endpoints } from '../../constants/routing';
import { IFetchCampaignSummariesResponsePayload } from '../../types/campaigns';
import { IResponse } from '../../types/http';
import {
  IActivateOneLinkSendRequestPayload,
  IDisableOneLinkSendRequestPayload,
  IDisableOneLinkSendSuccessPayload,
  IEditOneLinkRequestPayload,
  IFetchOneLinkByIdRequestMetadata,
  IFetchOneLinkByIdRequestPayload,
  IFetchOneLinkByIdResponsePayload,
  IFetchOneLinkSendsRequestPayload,
  IFetchOneLinkSendsSuccessPayload,
  IPopulateOneLinkCampaignSummariesRequestPayload,
  IPopulateOneLinkCampaignSummariesResponsePayload,
  OneLinkStatusEnum,
} from '../../types/oneLink';
import { IReduxAction } from '../../types/redux';
import { IApiError } from '../../types/shell';
import { splitPickCampaignIds } from '../../utils/marketplaceSend';
import { buildEndpointRoute } from '../../utils/routing';
import * as CampaignActions from '../actions/campaigns';
import * as OneLinkActions from '../actions/oneLink';
import { AuthRequest } from './helpers';

export function* oneLinkSendsWorkerSaga(action: IReduxAction<IFetchOneLinkSendsRequestPayload>) {
  const { page_size, department_ids, item_id, campaign_id, page, status, resolve, reject, search_query } =
    action.payload || {};

  try {
    const endpoint = `${monolithApiUrl}${endpoints.getOneLinkSends}`;
    const body = JSON.stringify({
      page_size,
      ...(department_ids?.length ? { department_ids } : {}),
      ...(item_id ? { item_id } : {}),
      ...(campaign_id ? { campaign_id } : {}),
      ...(search_query ? { search_query } : {}),
      status,
      page,
    });

    const response: IResponse<IFetchOneLinkSendsSuccessPayload | IApiError> = yield call(AuthRequest, {
      endpoint,
      method: 'POST',
      body,
    });
    if (!response.ok) {
      yield put(OneLinkActions.fetchOneLinkSendsFailure(response.body as IApiError));
      if (reject) {
        yield call(reject, response.body);
      }
    } else {
      yield put(OneLinkActions.fetchOneLinkSendsSuccess({ ...(response.body as IFetchOneLinkSendsSuccessPayload) }));
      if (resolve) {
        yield call(resolve, response.body);
      }
    }
  } catch (error) {
    yield put(OneLinkActions.fetchOneLinkSendsFailure(error));
    if (reject) {
      yield call(reject, error);
    }
  }
}

export function* oneLinkSendsByIdWorkerSaga(
  action: IReduxAction<IFetchOneLinkByIdRequestPayload, IFetchOneLinkByIdRequestMetadata>,
) {
  const { itemId, status = OneLinkStatusEnum.Active, resolve, reject } = action.payload || {};

  try {
    if (!itemId) {
      throw new Error('Item ID is required');
    }

    const endpoint = `${monolithApiUrl}${buildEndpointRoute(endpoints.getOneLinkSendById, { id: itemId })}`;

    const response: IResponse<IFetchOneLinkByIdResponsePayload | IApiError> = yield call<any>(AuthRequest, {
      endpoint,
      method: 'GET',
    });
    if (!response.ok) {
      if (reject) {
        reject(response.body);
      }
      yield put(OneLinkActions.fetchOneLinkByIdFailure(response.body as IApiError));
    } else {
      if (resolve) {
        resolve(response.body);
      }
      yield put(
        OneLinkActions.fetchOneLinkByIdSuccess(
          { ...(response.body as IFetchOneLinkByIdResponsePayload), status },
          action.metadata,
        ),
      );
    }
  } catch (error) {
    if (reject) {
      reject(error);
    }
    yield put(OneLinkActions.fetchOneLinkByIdFailure(error));
  }
}

export function* populateOneLinkCampaignSummariesWorkerSaga(
  action: IReduxAction<IPopulateOneLinkCampaignSummariesRequestPayload>,
): Generator<any, any, any> {
  try {
    const { itemId, status, campaign_ids } = action.payload!;
    const { campaignIds } = splitPickCampaignIds(campaign_ids);

    if (!campaignIds.length) {
      yield put(OneLinkActions.populateOneLinkCampaignSummariesSuccess({ campaign_summaries: [], itemId, status }));
      return;
    }

    yield put(CampaignActions.fetchCampaignSummariesRequest({ campaign_ids: campaignIds }));

    const [successAction, failureAction] = yield race([
      take(CampaignActions.FETCH_CAMPAIGN_SUMMARIES_SUCCESS),
      take(CampaignActions.FETCH_CAMPAIGN_SUMMARIES_FAILURE),
    ]);

    if (successAction) {
      yield put(
        OneLinkActions.populateOneLinkCampaignSummariesSuccess({
          ...((successAction as IReduxAction<IPopulateOneLinkCampaignSummariesResponsePayload>)
            .payload as IFetchCampaignSummariesResponsePayload),
          itemId,
          status,
        }),
      );
    } else {
      yield put(OneLinkActions.populateOneLinkCampaignSummariesFailure(failureAction.payload as IApiError));
    }
  } catch (error) {
    yield put(OneLinkActions.populateOneLinkCampaignSummariesFailure(error));
  }
}

export function* disableOneLinkSendSaga(action: IReduxAction<IDisableOneLinkSendRequestPayload>) {
  const { uid, resolve, reject } = action.payload || {};
  try {
    if (!uid) {
      throw new Error('An error has occurred when was trying to disable One Link Send');
    }

    const endpoint = `${monolithApiUrl}${buildEndpointRoute(endpoints.disableOneLinkSend, { id: uid })}`;

    const response: IResponse<never | IApiError> = yield call(AuthRequest, {
      endpoint,
      method: 'POST',
    });

    if (!response.ok) {
      reject?.(response.body);
      yield put(OneLinkActions.disableOneLinkFailure(response.body as IApiError));
    } else {
      resolve?.(uid);
      yield put(OneLinkActions.disableOneLinkSuccess({ uid } as IDisableOneLinkSendSuccessPayload));
    }
  } catch (e) {
    reject?.(e);
    yield put(OneLinkActions.disableOneLinkFailure(e));
  }
}

export function* activateOneLinkSendSaga(action: IReduxAction<IActivateOneLinkSendRequestPayload>) {
  const { uid, resolve, reject } = action.payload || {};
  try {
    if (!uid) {
      throw new Error('An error has occurred when was trying to activate One Link Send');
    }

    const endpoint = `${monolithApiUrl}${buildEndpointRoute(endpoints.activateOneLinkSend, { id: uid })}`;

    const response: IResponse<never | IApiError> = yield call(AuthRequest, {
      endpoint,
      method: 'POST',
    });

    if (!response.ok) {
      reject?.(response.body);
      yield put(OneLinkActions.activateOneLinkFailure(response.body as IApiError));
    } else {
      // Successful response is 204 No Content.
      resolve?.(uid);
      yield put(OneLinkActions.activateOneLinkSuccess({ uid }));
    }
  } catch (e) {
    reject?.(e);
    yield put(OneLinkActions.activateOneLinkFailure(e));
  }
}

export function* editOneLinkWorkerSaga(action: IReduxAction<IEditOneLinkRequestPayload>): Generator<any, any, any> {
  const { resolve, reject, oneLink } = action.payload || {};
  try {
    if (oneLink) {
      const endpoint = `${monolithApiUrl}${buildEndpointRoute(endpoints.editOneLink, { id: oneLink.uid })}`;

      const body = JSON.stringify(oneLink);

      const response = yield call<any>(AuthRequest, {
        method: 'PATCH',
        endpoint,
        body,
      });

      if (!response.ok) {
        yield put(OneLinkActions.editOneLinkFailure(response.body));
        if (reject) {
          yield call(reject, response.body);
        }
      } else {
        const { uid, status, ...rest } = oneLink;
        yield put(OneLinkActions.editOneLinkSuccess({ uid, status, chunk: Object.values(rest)[0] }));
        if (resolve) {
          const { error } = response.body || {};
          yield call(resolve, error);
        }
      }
    } else if (reject) {
      yield call(reject, { message: 'An error has occurred! No One Link to edit' });
    }
  } catch (error) {
    if (reject) {
      yield call(reject, error);
    }
  }
}

const sagas = {
  *watchOneLinkSendsRequest() {
    yield takeLatest(OneLinkActions.FETCH_ONE_LINK_SENDS_REQUEST, oneLinkSendsWorkerSaga);
  },
  *watchOneLinkSendByIdRequest() {
    yield takeLatest(OneLinkActions.FETCH_ONE_LINK_BY_ID_REQUEST, oneLinkSendsByIdWorkerSaga);
  },
  *watchFetchOneLinkCampaignSummariesRequest() {
    yield takeLatest(
      OneLinkActions.POPULATE_ONE_LINK_CAMPAIGN_SUMMARIES_REQUEST,
      populateOneLinkCampaignSummariesWorkerSaga,
    );
  },
  *watchDisableOneLinkSendRequest() {
    yield takeEvery(OneLinkActions.DISABLE_ONE_LINK_SEND_REQUEST, disableOneLinkSendSaga);
  },
  *watchActivateOneLinkSendRequest() {
    yield takeEvery(OneLinkActions.ACTIVATE_ONE_LINK_SEND_REQUEST, activateOneLinkSendSaga);
  },
  *watchEditOneLinkRequest() {
    yield takeLatest(OneLinkActions.EDIT_ONE_LINK_REQUEST, editOneLinkWorkerSaga);
  },
};

export default sagas;
